import { useEffect, useRef } from "react";
import type { JSX } from "react";
import {
  Editor,
  rootCtx,
  defaultValueCtx,
  editorViewCtx,
  editorViewOptionsCtx,
} from "@milkdown/core";
import { nord } from "@milkdown/theme-nord";
import { commonmark } from "@milkdown/preset-commonmark";
import { gfm } from "@milkdown/preset-gfm";
import { listener, listenerCtx } from "@milkdown/plugin-listener";
import { prism } from "@milkdown/plugin-prism";
import { Milkdown, MilkdownProvider, useEditor } from "@milkdown/react";
import { $prose } from "@milkdown/utils";
import type { EditorView } from "prosemirror-view";
import { cn } from "../ui/cn";
import {
  useMentionEditorUI,
  type MentionSuggestionConfig,
} from "./markdown/useMentionEditorUI";
import { createMentionPlugin } from "./markdown/mentionPlugin";
import "./MarkdownEditor.css";

/** Strip HTML artifacts that Milkdown's serializer may produce */
function cleanMarkdown(md: string): string {
  return md.replace(/<br\s*\/?>/g, "");
}

export interface MarkdownEditorProps {
  readonly initialValue: string;
  readonly onChange: (markdown: string) => void;
  readonly placeholder?: string;
  readonly readOnly?: boolean;
  readonly className?: string;
  readonly autoFocus?: boolean;
  readonly mentionSuggestion?: MentionSuggestionConfig;
}

function MilkdownEditor({
  initialValue,
  onChange,
  readOnly,
  autoFocus,
  mentionSuggestion,
}: MarkdownEditorProps): JSX.Element {
  const initialValueRef = useRef(initialValue);
  const onChangeRef = useRef(onChange);
  const activeRef = useRef(true);
  const viewRef = useRef<EditorView | null>(null);

  onChangeRef.current = onChange;

  useEffect(() => {
    activeRef.current = true;
    return (): void => { activeRef.current = false; };
  }, []);

  const { pluginCallbacks, handleKeyDown, popup } = useMentionEditorUI({
    getView: (): EditorView | null => viewRef.current,
    mentionSuggestion,
  });

  useEditor((root) => {
    const editorInstance = Editor.make()
      .config(nord)
      .config((ctx) => {
        ctx.set(rootCtx, root);
        ctx.set(defaultValueCtx, initialValueRef.current);
        ctx.get(listenerCtx).markdownUpdated((_ctx, markdown) => {
          if (!activeRef.current) return;
          onChangeRef.current(cleanMarkdown(markdown));
        });
        ctx.update(editorViewOptionsCtx, (prev) => ({
          ...prev,
          editable: (): boolean => !readOnly,
          handleKeyDown: (view, event): boolean => handleKeyDown(view, event),
        }));
      })
      .use(commonmark)
      .use(gfm)
      .use(listener)
      .use(prism);

    if (mentionSuggestion) {
      editorInstance.use(
        $prose((): ReturnType<typeof createMentionPlugin> =>
          createMentionPlugin(pluginCallbacks),
        ),
      );
      editorInstance.config((ctx) => {
        // Stash the live EditorView for the mention UI hook to dispatch into.
        const root2 = ctx.get(rootCtx);
        if (root2 instanceof HTMLElement) {
          requestAnimationFrame(() => {
            try {
              viewRef.current = ctx.get(editorViewCtx);
            } catch {
              /* editor not fully booted yet — useEditor will retry. */
            }
          });
        }
      });
    }

    if (autoFocus) {
      editorInstance.config((ctx) => {
        const rootElement = ctx.get(rootCtx);
        if (rootElement instanceof HTMLElement) {
          requestAnimationFrame(() => {
            const proseMirror = rootElement.querySelector(".ProseMirror");
            if (proseMirror instanceof HTMLElement) {
              proseMirror.focus();
            }
          });
        }
      });
    }

    return editorInstance;
  }, []);

  return (
    <div
      className={cn(
        "milkdown-editor-wrapper",
        readOnly && "pointer-events-none opacity-80",
      )}
    >
      <Milkdown />
      {popup}
    </div>
  );
}

export function MarkdownEditor(props: MarkdownEditorProps): JSX.Element {
  return (
    <div className={cn("h-full", props.className)}>
      <MilkdownProvider>
        <MilkdownEditor {...props} />
      </MilkdownProvider>
    </div>
  );
}
